import * as THREE from 'three';
import { useMemo } from 'react';

function MultiLevel({ result, convRatio, drawingBaseDimensions }) {
  const beltWidth = convRatio * drawingBaseDimensions.belt_width;
  const windingThickness = convRatio * drawingBaseDimensions.winding_thickness;
  const firstConLength =
    result.connection_conveyor_height?.[0]?.length ?? drawingBaseDimensions.u1;
  const length = convRatio * firstConLength;
  const centerArcX = 0;
  const insideWind =
    drawingBaseDimensions.column_centre_to_inside_outer_guide -
    drawingBaseDimensions.belt_width;
  const centerArcY =
    convRatio * drawingBaseDimensions.column_centre_to_inside_outer_guide;
  const arcIntersection =
    convRatio *
    Math.sqrt(
      drawingBaseDimensions.column_centre_to_inside_outer_guide ** 2 -
        insideWind
    );
  const crossAngle = Math.acos(
    insideWind / drawingBaseDimensions.column_centre_to_inside_outer_guide
  );
  const slopeAngle = Math.atan(
    drawingBaseDimensions.pitch /
      (drawingBaseDimensions.column_centre_to_inside_outer_guide * 2 * Math.PI)
  );

  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;

  const createCornerShape = () => {
    const shape = new THREE.Shape();

    shape.moveTo(0, 0);
    shape.lineTo(length, 0);
    shape.lineTo(length, beltWidth);
    shape.lineTo(arcIntersection, beltWidth);
    shape.absarc(
      centerArcX,
      centerArcY,
      centerArcY,
      crossAngle - Math.PI / 2,
      (3 * Math.PI) / 2,
      true
    );
    shape.lineTo(0, 0);

    return shape;
  };

  const extrudeSettings = useMemo(
    () => ({
      steps: 1,
      depth: windingThickness, // Extrude along Y-axis by 2 units
      bevelEnabled: false,
    }),
    [windingThickness]
  );

  const cornerShape = useMemo(
    () => createCornerShape(),
    [length, beltWidth, arcIntersection, centerArcX, centerArcY, crossAngle]
  );

  const cornerGeometry = useMemo(
    () => new THREE.ExtrudeGeometry(cornerShape, extrudeSettings),
    [cornerShape, extrudeSettings]
  );

  const materials = [
    new THREE.MeshStandardMaterial({ color: 'black', side: THREE.DoubleSide }), // Top face
    new THREE.MeshStandardMaterial({ color: 'silver', side: THREE.DoubleSide }), // Side faces
    new THREE.MeshStandardMaterial({ color: 'black', side: THREE.DoubleSide }), // Bottom face
  ];

  return (
    <>
      {result.connection_conveyor_height?.map((con) => {
        const angle = THREE.MathUtils.degToRad(con.angle) * reverseConfig;
        const posX = Math.cos(angle) * centerArcY;
        const posZ = Math.sin(angle) * centerArcY;
        const reverseDirection = con.direction === 'divert' ? 1 : -1;
        const reverseTransport =
          drawingBaseDimensions.transport_direction === 'transport down'
            ? -1
            : 1;
        const reverse = reverseConfig * reverseDirection * reverseTransport;

        // const heightFactor = 1;
        const heightFactor = reverseDirection * reverseTransport === 1 ? -1 : 1;
        const thicknessAdd = reverse === -1 ? -windingThickness : 0;
        return (
          <mesh
            position={[
              posX,
              con.height * convRatio +
                heightFactor * Math.sin(slopeAngle) * length +
                thicknessAdd,
              posZ,
            ]}
            rotation={[
              ((reverse === 1 ? 1 : 3) * Math.PI) / 2 -
                slopeAngle * reverseConfig,
              Math.PI * 2 - angle,
              Math.PI / 2,
            ]}
            rotation-order="YXZ"
            geometry={cornerGeometry}
            material={materials}
          />
        );
      })}
    </>
  );
}

export default MultiLevel;
